<template lang="pug">
div
    v-card
        v-card-title Token Rate Manager
            v-spacer
            v-btn(color="primary" @click="openAddDialog")
                v-icon(left) mdi-plus
                | Add Rate Configuration

        v-card-text
            v-alert(type="info" dense outlined)
                | Configure token conversion rates per group or individual user. 
                | Priority: Individual → Group → Global → System Default.
                br
                | System defaults: 10 points per rate, conversion: 50→5000, 100→10000, 200→25000, 500→50000

        v-data-table(
            :headers="headers"
            :items="rates"
            :loading="loading"
            loading-text="Loading rate configurations..."
            :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
        )
            template(v-slot:item.target_type="{ item }")
                v-chip(small :color="getTypeColor(item.target_type)" dark) {{ item.target_type }}

            template(v-slot:item.target_id="{ item }")
                span(v-if="item.target_type === 'GLOBAL'") —
                span(v-else) {{ getTargetName(item) }}

            template(v-slot:item.points_per_rate="{ item }")
                v-chip(small color="success" dark)
                    v-icon(left small) mdi-star
                    | {{ item.points_per_rate }}

            template(v-slot:item.conversion_map="{ item }")
                span {{ formatConversion(item.conversion_map) }}

            template(v-slot:item.is_active="{ item }")
                v-switch(
                    v-model="item.is_active"
                    :true-value="1"
                    :false-value="0"
                    @change="toggleActive(item)"
                    dense
                    hide-details
                )

            template(v-slot:item.actions="{ item }")
                v-btn(icon small color="primary" @click="openEditDialog(item)" title="Edit")
                    v-icon mdi-pencil
                v-btn(icon small color="error" @click="confirmDelete(item)" title="Delete")
                    v-icon mdi-delete

    // Add/Edit Dialog
    v-dialog(v-model="dialog" max-width="700px" persistent)
        v-card
            v-card-title
                span.headline {{ isEditing ? 'Edit' : 'Add' }} Rate Configuration
            v-card-text
                v-form(ref="rateForm" v-model="validForm")
                    v-select(
                        v-model="form.target_type"
                        :items="targetTypes"
                        label="Target Type*"
                        :rules="[rules.required]"
                        :disabled="isEditing"
                    )

                    // Group selector
                    v-autocomplete(
                        v-if="form.target_type === 'GROUP'"
                        v-model="form.target_id"
                        :items="groups"
                        item-text="group_name"
                        item-value="id"
                        label="Select Group*"
                        :rules="[rules.required]"
                        :loading="loadingGroups"
                    )

                    // User selector  
                    v-autocomplete(
                        v-if="form.target_type === 'INDIVIDUAL'"
                        v-model="form.target_id"
                        :items="users"
                        :item-text="userDisplayName"
                        item-value="id"
                        label="Select User*"
                        :rules="[rules.required]"
                        :loading="loadingUsers"
                        :search-input.sync="userSearch"
                    )

                    v-text-field(
                        v-model.number="form.points_per_rate"
                        label="Points Per Rate*"
                        type="number"
                        :rules="[rules.required, rules.positive]"
                        hint="Points earned per survey/rating (default: 10)"
                        persistent-hint
                    )

                    v-divider.my-4

                    .subtitle-1.mb-2 Conversion Table
                    v-alert(type="info" dense outlined).mb-4
                        | Maps token amounts to point costs. E.g. "50 tokens = 5000 points"

                    v-row(v-for="(value, key, index) in form.conversion_map" :key="index")
                        v-col(cols="5")
                            v-text-field(
                                :value="key"
                                label="Tokens"
                                type="number"
                                dense
                                @input="updateConversionKey(key, $event)"
                            )
                        v-col(cols="5")
                            v-text-field(
                                v-model.number="form.conversion_map[key]"
                                label="Points Cost"
                                type="number"
                                dense
                            )
                        v-col(cols="2")
                            v-btn(icon small color="error" @click="removeConversionEntry(key)")
                                v-icon mdi-minus-circle

                    v-btn(text small color="primary" @click="addConversionEntry")
                        v-icon(left small) mdi-plus
                        | Add Entry

            v-card-actions
                v-spacer
                v-btn(text @click="closeDialog") Cancel
                v-btn(
                    color="primary"
                    :disabled="!validForm || processing"
                    :loading="processing"
                    @click="saveRate"
                )
                    v-icon(left) mdi-content-save
                    | {{ isEditing ? 'Update' : 'Create' }}

    // Delete Confirmation
    v-dialog(v-model="deleteDialog" max-width="400px")
        v-card
            v-card-title.headline Delete Rate Configuration?
            v-card-text This action cannot be undone.
            v-card-actions
                v-spacer
                v-btn(text @click="deleteDialog = false") Cancel
                v-btn(color="error" :loading="processing" @click="deleteRate")
                    v-icon(left) mdi-delete
                    | Delete
</template>

<script>
import feathersClient from '@/plugins/feathers-client';

export default {
    name: 'TokenRateManager',

    data() {
        return {
            loading: false,
            processing: false,
            dialog: false,
            deleteDialog: false,
            isEditing: false,
            validForm: false,
            rates: [],
            groups: [],
            users: [],
            loadingGroups: false,
            loadingUsers: false,
            userSearch: '',
            selectedItem: null,

            form: {
                target_type: 'GLOBAL',
                target_id: null,
                points_per_rate: 10,
                conversion_map: { '50': 5000, '100': 10000, '200': 25000, '500': 50000 }
            },

            targetTypes: ['GLOBAL', 'GROUP', 'INDIVIDUAL'],

            headers: [
                { text: 'ID', value: 'id', width: 80 },
                { text: 'Type', value: 'target_type', width: 120 },
                { text: 'Target', value: 'target_id', width: 200 },
                { text: 'Points/Rate', value: 'points_per_rate', width: 130, align: 'center' },
                { text: 'Conversion Map', value: 'conversion_map', width: 300 },
                { text: 'Active', value: 'is_active', width: 100 },
                { text: 'Actions', value: 'actions', sortable: false, width: 120, align: 'center' }
            ],

            rules: {
                required: v => (v !== null && v !== undefined && v !== '') || 'This field is required',
                positive: v => v > 0 || 'Must be greater than 0'
            }
        };
    },

    watch: {
        'form.target_type'(val) {
            if (val === 'GROUP' && this.groups.length === 0) {
                this.fetchGroups();
            }
            if (val === 'INDIVIDUAL' && this.users.length === 0) {
                this.fetchUsers();
            }
            if (val === 'GLOBAL') {
                this.form.target_id = null;
            }
        },
        userSearch(val) {
            if (val && val.length >= 2) {
                this.searchUsers(val);
            }
        }
    },

    created() {
        this.fetchRates();
    },

    methods: {
        async fetchRates() {
            this.loading = true;
            try {
                const result = await feathersClient.service('bh-token-rates').find({
                    query: { $sort: { id: -1 }, $limit: 100 }
                });
                this.rates = result.data || result;
            } catch (error) {
                console.error('Error loading rates:', error);
                this.$toast.error('Failed to load rates');
            } finally {
                this.loading = false;
            }
        },

        async fetchGroups() {
            this.loadingGroups = true;
            try {
                const result = await feathersClient.service('bh-user-groups').find({
                    query: { $limit: 200, $sort: { group_name: 1 } }
                });
                this.groups = result.data || result;
            } catch (error) {
                console.error('Error loading groups:', error);
            } finally {
                this.loadingGroups = false;
            }
        },

        async fetchUsers() {
            this.loadingUsers = true;
            try {
                const result = await feathersClient.service('users').find({
                    query: { status: 1, $limit: 50, $sort: { fname: 1 } }
                });
                this.users = result.data || result;
            } catch (error) {
                console.error('Error loading users:', error);
            } finally {
                this.loadingUsers = false;
            }
        },

        async searchUsers(query) {
            this.loadingUsers = true;
            try {
                const result = await feathersClient.service('users').find({
                    query: {
                        status: 1,
                        $limit: 20,
                        $or: [
                            { fname: { $like: `%${query}%` } },
                            { lname: { $like: `%${query}%` } },
                            { email: { $like: `%${query}%` } }
                        ]
                    }
                });
                this.users = result.data || result;
            } catch (error) {
                console.error('Error searching users:', error);
            } finally {
                this.loadingUsers = false;
            }
        },

        userDisplayName(item) {
            return `${item.fname || ''} ${item.lname || ''} (${item.email || 'no email'})`.trim();
        },

        getTypeColor(type) {
            switch (type) {
                case 'GLOBAL': return 'purple';
                case 'GROUP': return 'blue';
                case 'INDIVIDUAL': return 'orange';
                default: return 'grey';
            }
        },

        getTargetName(item) {
            if (item.target_type === 'GROUP') {
                const group = this.groups.find(g => g.id === item.target_id);
                return group ? group.group_name : `Group #${item.target_id}`;
            }
            return `User #${item.target_id}`;
        },

        formatConversion(map) {
            if (!map) return '—';
            return Object.entries(map)
                .map(([tokens, points]) => `${tokens}→${Number(points).toLocaleString()}`)
                .join(', ');
        },

        openAddDialog() {
            this.isEditing = false;
            this.form = {
                target_type: 'GLOBAL',
                target_id: null,
                points_per_rate: 10,
                conversion_map: { '50': 5000, '100': 10000, '200': 25000, '500': 50000 }
            };
            this.dialog = true;
        },

        openEditDialog(item) {
            this.isEditing = true;
            this.selectedItem = item;
            this.form = {
                target_type: item.target_type,
                target_id: item.target_id,
                points_per_rate: item.points_per_rate,
                conversion_map: { ...item.conversion_map }
            };
            this.dialog = true;
        },

        closeDialog() {
            this.dialog = false;
            this.selectedItem = null;
            if (this.$refs.rateForm) {
                this.$refs.rateForm.reset();
            }
        },

        addConversionEntry() {
            const nextKey = Math.max(...Object.keys(this.form.conversion_map).map(Number), 0) + 50;
            this.$set(this.form.conversion_map, String(nextKey), 0);
        },

        removeConversionEntry(key) {
            this.$delete(this.form.conversion_map, key);
        },

        updateConversionKey(oldKey, newKey) {
            if (newKey && newKey !== oldKey) {
                const value = this.form.conversion_map[oldKey];
                this.$delete(this.form.conversion_map, oldKey);
                this.$set(this.form.conversion_map, String(newKey), value);
            }
        },

        async toggleActive(item) {
            try {
                await feathersClient.service('bh-token-rates').patch(item.id, {
                    is_active: item.is_active
                });
                this.$toast.success('Rate status updated');
            } catch (error) {
                console.error('Error updating status:', error);
                this.$toast.error('Failed to update status');
            }
        },

        async saveRate() {
            if (!this.$refs.rateForm.validate()) return;

            this.processing = true;
            try {
                const data = {
                    target_type: this.form.target_type,
                    target_id: this.form.target_type === 'GLOBAL' ? null : this.form.target_id,
                    points_per_rate: this.form.points_per_rate,
                    conversion_map: this.form.conversion_map,
                    is_active: 1
                };

                if (this.isEditing) {
                    await feathersClient.service('bh-token-rates').patch(this.selectedItem.id, data);
                    this.$toast.success('Rate configuration updated');
                } else {
                    await feathersClient.service('bh-token-rates').create(data);
                    this.$toast.success('Rate configuration created');
                }

                this.closeDialog();
                this.fetchRates();
            } catch (error) {
                console.error('Error saving rate:', error);
                this.$toast.error('Failed to save: ' + (error.message || 'Unknown error'));
            } finally {
                this.processing = false;
            }
        },

        confirmDelete(item) {
            this.selectedItem = item;
            this.deleteDialog = true;
        },

        async deleteRate() {
            this.processing = true;
            try {
                await feathersClient.service('bh-token-rates').remove(this.selectedItem.id);
                this.$toast.success('Rate configuration deleted');
                this.deleteDialog = false;
                this.fetchRates();
            } catch (error) {
                console.error('Error deleting rate:', error);
                this.$toast.error('Failed to delete');
            } finally {
                this.processing = false;
            }
        }
    }
};
</script>

<style scoped>
.v-chip {
    font-weight: 600;
}
</style>
